import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined';
import AirOutlinedIcon from '@mui/icons-material/AirOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import DeviceThermostatOutlinedIcon from '@mui/icons-material/DeviceThermostatOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import CompressOutlinedIcon from '@mui/icons-material/CompressOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import { Link, useParams } from 'react-router-dom';
import MachineAdminShell from './MachineAdminShell';
import useMockMachineObserver, { TIME_RANGE_OPTIONS } from './useMockMachineObserver';

function timelineColor(type) {
  if (type === 'critical') return '#ff5f57';
  if (type === 'warning') return '#ffb44c';
  if (type === 'normal') return '#4ade80';
  return '#60a5fa';
}

const sensorCatalog = {
  power: { label: 'Elektrik Gücü', icon: <ElectricBoltOutlinedIcon sx={{ color: '#fbbf24' }} /> },
  airflowInstant: { label: 'Hava Akışı', icon: <AirOutlinedIcon sx={{ color: '#7dd3fc' }} /> },
  airTotal: { label: 'Hava Tüketimi', icon: <CompressOutlinedIcon sx={{ color: '#c4b5fd' }} /> },
  pressure: { label: 'Basınç', icon: <SpeedOutlinedIcon sx={{ color: '#fca5a5' }} /> },
  temperature: { label: 'Sıcaklık', icon: <DeviceThermostatOutlinedIcon sx={{ color: '#fb7185' }} /> },
  humidity: { label: 'Nem', icon: <WaterDropOutlinedIcon sx={{ color: '#4ade80' }} /> },
};

export default function MachineDetailMockPage() {
  const { machineId } = useParams();
  const [timeRange, setTimeRange] = React.useState('1m');
  const { chartTimes, liveDetail: machine, timeRangeConfig } = useMockMachineObserver(machineId, timeRange);
  const [selectedSensor, setSelectedSensor] = React.useState('pressure');
  const activeAlertsCount = Array.isArray(machine.activeAlerts) ? machine.activeAlerts.length : machine.activeAlerts;
  const systemComment =
    machine.state === 'alarm'
      ? 'Sistem yorumu: yüksek hava akışı ile düşük basınç birlikte ilerliyor. Kaçak, regülatör problemi veya filtre tıkanıklığı olasılığı yüksek.'
      : machine.state === 'warning'
        ? 'Sistem yorumu: sıcaklık üst sınıra yaklaşıyor. Soğutma, sürtünme veya yük dengesi kontrol edilirse alarm önlenebilir.'
        : 'Sistem yorumu: sensör verileri nominal aralıkta ilerliyor. Makine stabil görünüyor.';
  const referenceRanges = [
    { label: 'Elektrik Gücü', current: machine.power, range: '6.5 - 8.8 kW', status: machine.rawMetrics.power > 8.8 ? 'Üstünde' : 'Normal' },
    { label: 'Hava Akışı', current: machine.airflowInstant, range: '160 - 240 L/dk', status: machine.rawMetrics.airflowInstant > 240 ? 'Üstünde' : 'Normal' },
    { label: 'Basınç', current: machine.pressure, range: '5.8 - 6.6 bar', status: machine.rawMetrics.pressure < 5.8 ? 'Altında' : 'Normal' },
    { label: 'Sıcaklık', current: machine.temperature, range: '38 - 52 °C', status: machine.rawMetrics.temperature > 52 ? 'Üstünde' : 'Normal' },
    { label: 'Nem', current: machine.humidity, range: '40 - 55 %RH', status: machine.rawMetrics.humidity < 40 || machine.rawMetrics.humidity > 55 ? 'Dışında' : 'Normal' },
  ];

  const chart = machine.sensorCharts[selectedSensor];
  const series = [{ data: chart.values, color: chart.color, area: true, curve: 'monotoneX', showMark: false }];

  if (chart.thresholdValue !== null) {
    series.push({
      data: chart.values.map(() => chart.thresholdValue),
      color: '#ffb44c',
      curve: 'linear',
      showMark: false,
    });
  }

  const sensorSummaryCards = [
    { key: 'power', value: machine.power },
    { key: 'airflowInstant', value: machine.airflowInstant },
    { key: 'airTotal', value: machine.airTotal },
    { key: 'pressure', value: machine.pressure },
    { key: 'temperature', value: machine.temperature },
    { key: 'humidity', value: machine.humidity },
  ];

  return (
    <MachineAdminShell
      pageTitle={`${machine.name} · Makine Detayı`}
      pageSubtitle="Canlı durumu, sensör trendlerini, aktif alarmları ve önerilen operatör aksiyonlarını aynı ekranda görün."
      pageActions={
        <Button
          component={Link}
          to="/machine-admin"
          startIcon={<ArrowBackOutlinedIcon />}
          variant="outlined"
          sx={{
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.18)',
            bgcolor: 'rgba(255,255,255,0.06)',
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 2.5,
            '&:hover': {
              borderColor: 'rgba(255,255,255,0.28)',
              bgcolor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          Genel izlemeye dön
        </Button>
      }
    >
      <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, background: `linear-gradient(135deg, ${alpha(machine.severityColor, 0.34)}, rgba(41,15,15,0.95))`, border: `1px solid ${alpha(machine.severityColor, 0.45)}`, color: '#fff' }}>
        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} justifyContent="space-between">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <NotificationsActiveOutlinedIcon sx={{ color: alpha(machine.severityColor, 0.92) }} />
              <Typography variant="h4" sx={{ fontWeight: 900 }}>
                {machine.headline}
              </Typography>
              <Chip label={machine.severity} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 700 }} />
            </Stack>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.86)' }}>
              {machine.detail}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
            {[
              { label: 'Aktif Alarm', value: `${activeAlertsCount} adet` },
              { label: 'Son Veri', value: machine.lastSeen },
              { label: 'Sensör Yayını', value: '1 sn' },
            ].map((item) => (
              <Paper key={item.label} sx={{ minWidth: 150, p: 1.5, borderRadius: 2.5, bgcolor: 'rgba(3,7,18,0.35)', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                  {item.label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25 }}>
                  {item.value}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2 }}>
        {sensorSummaryCards.map((item) => (
          <Paper key={item.key} sx={{ p: 1.75, borderRadius: 3, bgcolor: 'rgba(15, 23, 32, 0.92)', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              {sensorCatalog[item.key].icon}
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.66)' }}>
                  {sensorCatalog[item.key].label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.3 }}>
                  {item.value}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Box>

      <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.5} alignItems="stretch">
        <Box sx={{ flex: 1.6 }}>
          <Paper sx={{ p: 2.25, borderRadius: 3, bgcolor: 'rgba(15, 23, 32, 0.92)', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography variant="overline" sx={{ color: '#94a3b8', letterSpacing: 1.2 }}>
              Canlı Trend
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1.25, mb: 1.5 }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.62)' }}>
                Zaman aralığı
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {TIME_RANGE_OPTIONS.map((option) => (
                  <Button
                    key={option.key}
                    size="small"
                    variant={timeRange === option.key ? 'contained' : 'outlined'}
                    onClick={() => setTimeRange(option.key)}
                    sx={{
                      minWidth: 72,
                      fontWeight: 700,
                      textTransform: 'none',
                      borderRadius: 2.5,
                      borderColor: timeRange === option.key ? '#1d4ed8' : 'rgba(255,255,255,0.14)',
                      bgcolor: timeRange === option.key ? '#1976d2' : 'rgba(255,255,255,0.06)',
                      color: '#fff',
                      '&:hover': {
                        borderColor: timeRange === option.key ? '#1e40af' : 'rgba(255,255,255,0.22)',
                        bgcolor: timeRange === option.key ? '#1565c0' : 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Stack>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1.25, mb: 2 }}>
              {Object.entries(machine.sensorCharts).map(([key, value]) => (
                <Button
                  key={key}
                  size="small"
                  variant={selectedSensor === key ? 'contained' : 'outlined'}
                  onClick={() => setSelectedSensor(key)}
                  sx={{
                    justifyContent: 'flex-start',
                    minWidth: 110,
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2.5,
                    borderColor: selectedSensor === key ? '#1d4ed8' : 'rgba(255,255,255,0.14)',
                    bgcolor: selectedSensor === key ? '#1976d2' : 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    '&:hover': {
                      borderColor: selectedSensor === key ? '#1e40af' : 'rgba(255,255,255,0.22)',
                      bgcolor: selectedSensor === key ? '#1565c0' : 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  {value.label}
                </Button>
              ))}
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {chart.label} Trendi
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.62)', mt: 0.4 }}>
                  Tarayıcının yerel saatine göre {timeRangeConfig.label} görünümü
                </Typography>
              </Box>
              <Stack spacing={0.5} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                <Typography variant="body1" sx={{ fontWeight: 800 }}>
                  Son değer: {chart.values[chart.values.length - 1]} {chart.unit}
                </Typography>
                {chart.thresholdLabel ? (
                  <Typography variant="body2" sx={{ color: '#ffcf88' }}>
                    {chart.thresholdLabel}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>

            <Box sx={{ mb: 2.5 }}>
              <LineChart
                height={320}
                xAxis={[{ scaleType: 'point', data: chartTimes, tickLabelStyle: { fill: '#94a3b8', fontSize: 11 } }]}
                yAxis={[{ tickLabelStyle: { fill: '#94a3b8', fontSize: 11 } }]}
                series={series}
                grid={{ horizontal: true, vertical: false }}
                margin={{ top: 20, right: 20, bottom: 30, left: 60 }}
                slotProps={{ legend: { hidden: true } }}
                sx={{
                  '.MuiChartsGrid-line': { stroke: 'rgba(255,255,255,0.08)' },
                  '.MuiAreaElement-root': { fill: `url(#machine-sensor-${selectedSensor})` },
                }}
              >
                <defs>
                  <linearGradient id={`machine-sensor-${selectedSensor}`} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={alpha(chart.color, 0.38)} />
                    <stop offset="100%" stopColor={alpha(chart.color, 0.04)} />
                  </linearGradient>
                </defs>
              </LineChart>
            </Box>

            <Paper sx={{ p: 2, borderRadius: 2.5, bgcolor: 'rgba(30,64,175,0.2)', color: '#e0ecff', border: '1px solid rgba(96,165,250,0.18)' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                <Box>
                  <Typography variant="overline" sx={{ color: '#93c5fd', letterSpacing: 1.2 }}>
                    Önerilen aksiyon
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                    {machine.suggestedActions.map((action) => (
                      <Typography key={action} variant="body1" sx={{ fontWeight: 700 }}>
                        {action}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<BuildOutlinedIcon />}
                  sx={{
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2.5,
                    bgcolor: '#1976d2',
                    '&:hover': { bgcolor: '#1565c0' },
                  }}
                >
                  Kontrol listesi aç
                </Button>
              </Stack>
            </Paper>
          </Paper>
        </Box>

        <Stack spacing={2.5} sx={{ flex: 1 }}>
          <Paper sx={{ p: 2.25, borderRadius: 3, bgcolor: 'rgba(15, 23, 32, 0.92)', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      Aktif Alarmlar ({activeAlertsCount})
                    </Typography>
                  </Stack>
                  <Stack spacing={1.25}>
                    {activeAlertsCount === 0 ? (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        Bu makinede aktif alarm bulunmuyor.
                      </Typography>
                    ) : (
                      machine.activeAlerts.map((alert) => (
                        <Paper key={alert.id} sx={{ p: 1.5, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.03)', border: `1px solid ${alpha(alert.severityColor, 0.25)}` }}>
                          <Stack direction="row" justifyContent="space-between" spacing={2}>
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <CircleIcon sx={{ color: alert.severityColor, fontSize: 12 }} />
                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#f8fafc' }}>
                                  {alert.title}
                                </Typography>
                                <Chip size="small" label={alert.severity} sx={{ bgcolor: alpha(alert.severityColor, 0.16), color: '#fff', fontWeight: 700 }} />
                              </Stack>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.68)' }}>
                                {alert.meta}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                {alert.status}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      ))
                    )}
                  </Stack>
                </Paper>

          <Paper sx={{ p: 2.25, borderRadius: 3, bgcolor: 'rgba(15, 23, 32, 0.92)', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Alert severity={machine.state === 'alarm' ? 'error' : machine.state === 'warning' ? 'warning' : 'success'} sx={{ mb: 2, bgcolor: machine.state === 'alarm' ? 'rgba(239,68,68,0.14)' : machine.state === 'warning' ? 'rgba(245,158,11,0.14)' : 'rgba(34,197,94,0.14)', color: machine.state === 'alarm' ? '#fee2e2' : machine.state === 'warning' ? '#fef3c7' : '#dcfce7', border: machine.state === 'alarm' ? '1px solid rgba(239,68,68,0.2)' : machine.state === 'warning' ? '1px solid rgba(245,158,11,0.2)' : '1px solid rgba(34,197,94,0.2)' }}>
              {systemComment}
            </Alert>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
              Normal çalışma aralıkları:
            </Typography>
            <Stack spacing={1} sx={{ mt: 1.25 }}>
              {referenceRanges.map((item) => (
                <Paper
                  key={item.label}
                  sx={{
                    p: 1.35,
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${
                      item.status === 'Normal'
                        ? 'rgba(255,255,255,0.08)'
                        : item.status === 'Altında'
                          ? 'rgba(239,68,68,0.28)'
                          : 'rgba(245,158,11,0.28)'
                    }`,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 800, color: '#f8fafc' }}>
                        {item.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.62)' }}>
                        Normal aralık: {item.range}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body1" sx={{ fontWeight: 800, color: '#f8fafc' }}>
                        {item.current}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            item.status === 'Normal'
                              ? '#86efac'
                              : item.status === 'Altında'
                                ? '#fca5a5'
                                : '#fcd34d',
                          fontWeight: 700,
                        }}
                      >
                        {item.status}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.25, borderRadius: 3, bgcolor: 'rgba(15, 23, 32, 0.92)', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <TimelineOutlinedIcon sx={{ color: '#cbd5e1' }} />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Olay Zaman Çizelgesi
              </Typography>
            </Stack>
            <Stack spacing={1.75}>
              {machine.timeline.map((item, index) => (
                <Box key={`${item.time}-${item.label}`} sx={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CircleIcon sx={{ color: timelineColor(item.type), fontSize: 14 }} />
                    {index < machine.timeline.length - 1 ? <Box sx={{ width: 2, flex: 1, bgcolor: 'rgba(255,255,255,0.1)', my: 0.5 }} /> : null}
                  </Box>
                  <Box sx={{ pb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.52)' }}>
                      {item.time}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.25 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {item.detail}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Stack>
    </MachineAdminShell>
  );
}
